/**
 * Tiny fetch wrapper. Same-origin /api + /auth (Vite proxies to the worker in
 * dev; Pages + Worker share an origin in prod), so cookies ride along with
 * credentials: 'include'.
 */

export class ApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message?: string) {
    super(message ?? code);
    this.status = status;
    this.code = code;
  }
}

/** Device LOCAL calendar date (YYYY-MM-DD), sent on every request so the server
 *  resolves "today" in the user's timezone instead of UTC. */
function localDate(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Device LOCAL wall-clock time (HH:MM) — lets the server hide today's already-
 *  passed booking slots against the user's clock, not the server's UTC clock. */
function localTime(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { 'x-local-date': localDate(), 'x-local-time': localTime() };
  if (body) headers['content-type'] = 'application/json';
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  const json = text ? JSON.parse(text) : {};

  if (!res.ok) {
    throw new ApiError(res.status, (json as { error?: string }).error ?? 'request_failed');
  }
  return json as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
};
